import clsx from "clsx";
import {
  REVIEW_DIFF_ADDED,
  REVIEW_DIFF_REMOVED,
} from "@/components/review/reviewSurfaces";
import { TEXT_MUTED, TEXT_ROW_TITLE } from "@/components/ui/surface/surfaceTokens";
import {
  buildFieldDiffRows,
  buildWordDiffSpans,
  type EditDiffSpan,
  type FieldDiffRow,
} from "./editDiffUtils";

export type EditDiffProps = {
  mode?: "text" | "fields";
  before?: string;
  after?: string;
  beforeFields?: Record<string, unknown>;
  afterFields?: Record<string, unknown>;
  className?: string;
};

function DiffSpanView({ span }: { span: EditDiffSpan }) {
  if (span.kind === "same") {
    return <span>{span.text}</span>;
  }
  return (
    <span className={span.kind === "remove" ? REVIEW_DIFF_REMOVED : REVIEW_DIFF_ADDED}>
      {span.text}
    </span>
  );
}

function FieldDiffBlock({ row }: { row: FieldDiffRow }) {
  const beforeTrim = row.before.trim();
  const afterTrim = row.after.trim();
  const useSplit = beforeTrim.length > 0 && afterTrim.length > 0 && beforeTrim !== afterTrim;

  return (
    <div className="space-y-1" data-testid={`edit-diff-field-${row.field}`}>
      <p className={TEXT_ROW_TITLE}>{row.field}</p>
      {useSplit ? (
        <div className="space-y-2">
          <p className={clsx("text-sm leading-relaxed whitespace-pre-wrap", REVIEW_DIFF_REMOVED)} data-testid="edit-diff-removed">
            {row.before}
          </p>
          <p className={clsx("text-sm leading-relaxed whitespace-pre-wrap", REVIEW_DIFF_ADDED)} data-testid="edit-diff-added">
            {row.after}
          </p>
        </div>
      ) : (
        <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">
          {row.spans.map((span, index) => (
            <DiffSpanView key={`${row.field}-${index}`} span={span} />
          ))}
        </p>
      )}
    </div>
  );
}

export function EditDiff({
  mode = "text",
  before = "",
  after = "",
  beforeFields = {},
  afterFields = {},
  className,
}: EditDiffProps) {
  const textSpans = mode === "text" ? buildWordDiffSpans(before, after) : [];
  const fieldRows = mode === "fields" ? buildFieldDiffRows(beforeFields, afterFields) : [];

  const empty =
    mode === "text"
      ? textSpans.every((span) => span.kind === "same" && !span.text.trim())
      : fieldRows.length === 0;

  if (empty) {
    return (
      <p className={clsx(TEXT_MUTED, className)} data-testid="edit-diff-empty">
        No edits recorded.
      </p>
    );
  }

  const beforeTrim = before.trim();
  const afterTrim = after.trim();
  const textSplit = mode === "text" && beforeTrim.length > 0 && afterTrim.length > 0 && beforeTrim !== afterTrim;

  return (
    <div className={clsx("space-y-3", className)} data-testid="edit-diff">
      {mode === "text" ? (
        textSplit ? (
          <div className="space-y-2">
            <p
              className={clsx("text-sm leading-relaxed whitespace-pre-wrap", REVIEW_DIFF_REMOVED)}
              data-testid="edit-diff-removed"
            >
              {before}
            </p>
            <p
              className={clsx("text-sm leading-relaxed whitespace-pre-wrap", REVIEW_DIFF_ADDED)}
              data-testid="edit-diff-added"
            >
              {after}
            </p>
          </div>
        ) : (
          <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">
            {textSpans.map((span, index) => (
              <DiffSpanView key={index} span={span} />
            ))}
          </p>
        )
      ) : (
        fieldRows.map((row) => <FieldDiffBlock key={row.field} row={row} />)
      )}
    </div>
  );
}
